import { useForm } from "react-hook-form";
import { Box, debounce, Paper } from "@mui/material";
// import type { Form } from "../../types/Form";
import { TextBlocksRenderer } from "../TextBlocksRenderer";
import { DynamicFormSelect } from "./DynamicFormSelect";
import { DynamicFormTextArea } from "./DynamicFormTextArea";
import { DynamicFormMultiSelect } from "./DynamicFormMultiSelect";
import { clearFormState, saveFormState } from "utils";
import { FormsApi, type DynamicForm as Form, type FormSubmit } from "api";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { DynamicFormContactInfo } from "./DynamicFormContactInfo";
import { DynamicFormPage } from "./DynamicFormPage";
import { AnimatePresence } from "framer-motion";
import type { DynamicFormPages } from "./types";
import { DynamicFormNewsletter } from "./DynamicFormNewsletter";
import { DynamicFormFieldHeading } from "./DynamicFormFieldHeading";
import { DynamicFormStepHeading } from "./DynamicFormStepHeading";
import { useLocation, useNavigate, useParams } from "react-router";
import { useErrorContext } from "../../contexts/error/useErrorContext";
import { useApi } from "../../hooks/useApi";
import { track } from "../../utils/analytics";

type Props = {
  dynamicForm: Form;
  session: Partial<FormSubmit> & Pick<FormSubmit, "sessionId">;
};

type FormData = FormSubmit;

export const DynamicForm = ({ dynamicForm, session }: Props) => {
  const [, , removeCookie] = useCookies([`form-session-${dynamicForm.documentId}`]);
  const [seenPages, setSeenPages] = useState<number[]>(() => {
    const seen: number[] = [];
    let idx = 0;

    const contactComplete = !!(
      session.contactInfo?.firstName &&
      session.contactInfo?.lastName &&
      session.contactInfo?.email
    );
    const stepsHaveData =
      session.steps?.some((s) =>
        s.questions?.some((q) => q.answers && q.answers.length > 0),
      ) ?? false;

    // Intro — seen if the user is returning with substantive saved data
    if (contactComplete || stepsHaveData) seen.push(idx);
    idx++;

    // Contact info (index 1, always in array regardless of useContact)
    if (contactComplete) seen.push(idx);
    idx++;

    // Steps — only mark seen when every required question already has an answer
    dynamicForm.steps.forEach((step, si) => {
      const stepSession = session.steps?.at(si);
      const allRequiredAnswered = step.questions.every((q, qi) => {
        if (!q.isRequired) return true;
        return (stepSession?.questions?.at(qi)?.answers?.length ?? 0) > 0;
      });
      const hasSomeData = step.questions.some(
        (_, qi) => (stepSession?.questions?.at(qi)?.answers?.length ?? 0) > 0,
      );
      if (allRequiredAnswered && hasSomeData) seen.push(idx);
      idx++;
    });

    // Newsletter (always last in array regardless of useNewsletter)
    if (session.newsletterOptIn != null) seen.push(idx);

    return seen;
  });
  const [dirtyPages, setDirtyPages] = useState<number[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { pageIndex: pageIndexParam } = useParams<{ pageIndex?: string }>();
  const activePage = parseInt(pageIndexParam ?? "0", 10);
  const { setError } = useErrorContext();
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const [pageContainerPadding, setPageContainerPadding] = useState(0);
  const formStartedRef = useRef(false);

  const api = useApi(FormsApi);

  const basePath = location.pathname.replace(/\/page\/\d+$/, "");
  const navigateToPage = useCallback(
    (index: number) => {
      if (index === 0) {
        navigate(basePath);
      } else {
        navigate(`${basePath}/page/${index}`);
      }
    },
    [navigate, basePath],
  );

  const form = useForm<FormData>({
    mode: "all",
    shouldUnregister: false,
    criteriaMode: "all",
    defaultValues: {
      sessionId: session.sessionId,
      documentId: dynamicForm.documentId,
      contactInfo: {
        firstName: session.contactInfo?.firstName ?? "",
        lastName: session.contactInfo?.lastName ?? "",
        email: session.contactInfo?.email ?? "",
      },
      steps: dynamicForm.steps.map((step, si) => ({
        id: step.id,
        questions: step.questions.map((q, qi) => ({
          id: q.id,
          isUserInput: q.isUserInput,
          answers: session.steps?.at(si)?.questions?.at(qi)?.answers ?? [],
        })),
      })),
      newsletterOptIn: session.newsletterOptIn,
    },
  });

  useEffect(() => {
    const title = dynamicForm.title || "Untitled Form";
    document.title = title;
    return () => {
      document.title = "Dynamic Forms";
    };
  }, [dynamicForm.title]);

  useEffect(() => {
    track("form_view", {
      formId: dynamicForm.documentId,
      formTitle: dynamicForm.title,
    });
  }, [dynamicForm.documentId, dynamicForm.title]);

  useEffect(() => {
    if (activePage > 0 && !formStartedRef.current) {
      formStartedRef.current = true;
      track("form_started", {
        formId: dynamicForm.documentId,
        formTitle: dynamicForm.title,
      });
    }
    track("form_page_view", {
      formId: dynamicForm.documentId,
      formTitle: dynamicForm.title,
      pageIndex: activePage,
      pageName: pages[activePage]?.name,
    });
    // activePage is the only trigger we need; other values are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  const { handleSubmit, control, watch, formState, trigger } = form;

  const watchAllFields = watch();

  const onUpdate = useCallback(
    async (data: FormData) => {
      console.log("auto-saving form data");
      saveFormState(session.sessionId, data);
      // await api.apiFormsUpdatedPost({ formSubmit: data });
    },
    [api, session.sessionId],
  );

  const debouncedUpdate = useMemo(() => debounce(onUpdate, 500), [onUpdate]);

  useEffect(() => {
    debouncedUpdate(watchAllFields);
  }, [watchAllFields, session.sessionId, debouncedUpdate]);

  const handleResize = () => {
    const container = pageContainerRef.current;
    if (!container) {
      setPageContainerPadding(0);
      return;
    }

    const lastQuestion = container.querySelector<HTMLElement>(
      ".scroll-item:last-of-type",
    );
    if (!lastQuestion) {
      setPageContainerPadding(0);
      return;
    }

    const containerHeight = container.getBoundingClientRect().height;
    const questionHeight = lastQuestion.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight;

    if (containerHeight > viewportHeight) {
      let diff = viewportHeight - questionHeight - 128;
      if (diff < 0) diff = 0;
      setPageContainerPadding(diff);
    } else {
      setPageContainerPadding(0);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activePage]);

  const onSubmit = async (data: FormData) => {
    console.log("onSubmit config:", dynamicForm.onSubmit);
    await api
      .apiFormsSubmitPost({ formSubmit: data })
      .then(() => {
        track("form_completed", {
          formId: dynamicForm.documentId,
          formTitle: dynamicForm.title,
        });
        const isDev = location.pathname.includes("/forms/dev/");
        if (!isDev) {
          removeCookie(`form-session-${dynamicForm.documentId}`, { path: "/" });
          clearFormState(session.sessionId);
        }
        if (dynamicForm.onSubmit.useRedirect && dynamicForm.onSubmit.url) {
          window.location.href = dynamicForm.onSubmit.url;
        } else {
          navigate(`${basePath}/thank-you`);
        }
      })
      .catch((error) => {
        track("form_submit_error", {
          formId: dynamicForm.documentId,
          formTitle: dynamicForm.title,
        });
        setError({
          title: "Submission Error",
          message:
            "There was an error submitting the form. Please try again later.",
        });
      });
  };

  const renderIntro = () => {
    return (
      <Box>
        <TextBlocksRenderer content={dynamicForm.intro} />
      </Box>
    );
  };

  const renderContact = () => {
    return (
      <Box>
        <DynamicFormContactInfo
          control={control}
          contactInfo={dynamicForm.contact}
        />
      </Box>
    );
  };
  const renderNewsletter = () => {
    return (
      <Box>
        <DynamicFormNewsletter
          control={control}
          newsletter={dynamicForm.newsletter}
        />
      </Box>
    );
  };

  const renderStep = (step: Form["steps"][number], stepIndex: number) => {
    const trackQuestion = (question: Form["steps"][number]["questions"][number]) => {
      track("form_question_answered", {
        formId: dynamicForm.documentId,
        formTitle: dynamicForm.title,
        stepTitle: step.title,
        questionId: question.id,
        questionTitle: question.title,
      });
    };

    return (
      <Box
        key={step.id}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box>
          <DynamicFormStepHeading>{step.title}</DynamicFormStepHeading>
          <TextBlocksRenderer content={step.header} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {step.questions.map((question, questionIndex) => (
            <Box
              key={question.id}
              className="scroll-item"
              sx={{ scrollMarginTop: "64px" }}
            >
              <Paper
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: 2,
                }}
                elevation={3}
              >
                <DynamicFormFieldHeading>
                  {question.title}
                </DynamicFormFieldHeading>
                <TextBlocksRenderer content={question.header} />
                <Box>
                  {question.isUserInput ? (
                    <DynamicFormTextArea
                      control={control}
                      name={`steps.${stepIndex}.questions.${questionIndex}.answers`}
                      required={question.isRequired}
                      isSingleLine={question.isSingleLine}
                      onAnswer={() => trackQuestion(question)}
                    />
                  ) : question.isMultipleChoice ? (
                    <DynamicFormMultiSelect
                      control={control}
                      name={`steps.${stepIndex}.questions.${questionIndex}.answers`}
                      options={question.answers || []}
                      required={question.isRequired}
                      onAnswer={() => trackQuestion(question)}
                    />
                  ) : (
                    <DynamicFormSelect
                      control={control}
                      name={`steps.${stepIndex}.questions.${questionIndex}.answers`}
                      options={question.answers || []}
                      required={question.isRequired}
                      onAnswer={() => trackQuestion(question)}
                    />
                  )}
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
        {step.useFooter && step.footer && (
          <Box mt={2}>
            <TextBlocksRenderer content={step.footer} />
          </Box>
        )}
      </Box>
    );
  };

  const p: Array<Omit<DynamicFormPages[number], "isSeen" | "isDirty">> = [
    { key: "intro", name: "Intro", fn: renderIntro, errors: undefined, active: true },
    {
      key: "contactInfo",
      name: "Contact Info",
      fn: dynamicForm.useContact ? renderContact : () => null,
      errors: formState.errors.contactInfo,
      active: dynamicForm.useContact ?? false,
    },
    ...dynamicForm.steps.map((step, stepIndex) => ({
      key: `steps.${stepIndex}` as const,
      name: step.title ?? `Step ${stepIndex + 1}`,
      fn: () => renderStep(step, stepIndex),
      errors: formState.errors.steps?.[stepIndex],
      active: true,
    })),
    {
      key: "newsletterOptIn",
      name: "Newsletter",
      fn: renderNewsletter,
      errors: formState.errors.newsletterOptIn,
      active: dynamicForm.useNewsletter ?? false,
    },
  ];
  const pages: DynamicFormPages = p.map((page, i) => ({
    ...page,
    isSeen: seenPages.includes(i),
    isDirty: dirtyPages.includes(i),
  }));

  const activePageKey = pages[activePage]?.key ?? "intro";

  if (!dynamicForm) return null;

  return (
    <Box ref={pageContainerRef}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingBottom: `${pageContainerPadding}px`,
          }}
        >
          <AnimatePresence mode="wait">
            <DynamicFormPage
              navigation={dynamicForm.navigation}
              trigger={trigger}
              onDirty={() => {
                setDirtyPages((prev) => {
                  if (!prev.includes(activePage)) {
                    return [...prev, activePage];
                  }
                  return prev;
                });
              }}
              // onPageTransitionEnd={() =>
              //   window.scrollTo({ top: 0, behavior: "smooth" })
              // }
              // onPageTransitionStart={
              //   () => {
              //     requestAnimationFrame(() => {
              //       window.scrollTo({ top: 0, behavior: "instant" });
              //     });
              //     // window.scroll({ top: 0, behavior: "smooth" });
              //   }
              //   // window.scrollTo({ top: 0, behavior: "smooth" })
              // }
              onRendered={() => {
                setSeenPages((prev) => {
                  if (!prev.includes(activePage)) {
                    return [...prev, activePage];
                  }
                  return prev;
                });
              }}
              onNavigate={(i) => {
                navigateToPage(i);
              }}
              pageKey={activePageKey}
              formState={formState}
              pageIndex={activePage}
              pages={pages}
              onPrevious={() => {
                navigateToPage(Math.max(activePage - 1, 0));
              }}
              onNext={() => {
                navigateToPage(Math.min(activePage + 1, pages.length - 1));
              }}
            >
              {pages[activePage]?.fn()}
            </DynamicFormPage>
          </AnimatePresence>
        </Box>
      </form>
    </Box>
  );
};
